import { CheckboxBlock } from '@/lib/widgets/catalog';
import { SaleCatalogMap } from '@/lib/widgets/sale-catalog/sale-catalog-map';

import { SALE_OPTIONS } from '@/lib/entities/catalog/constants';
import { getTranslatedOptionsFn } from '@/lib/entities/catalog/lib/get-translated-options';
import { PriceControls } from '@/lib/entities/catalog/ui';
import { useCurrency } from '@/lib/entities/currency';
import { useSaleQuery } from '@/lib/entities/sale/api';
import { useSaleCatalogQuery } from '@/lib/entities/sale/lib';

import { Button } from '@/lib/shared/components/Buttons';
import { modal } from '@/lib/shared/components/Modal';
import { P2 } from '@/lib/shared/components/Typography';
import { MapPinIcon } from '@/lib/shared/icons/MapPinIcon';
import { SvgPerfectInvest } from '@/lib/shared/icons/catalog-filter';
import { getWordEnding } from '@/lib/shared/utils/get-word-ending';

import { clsx } from 'clsx';
import { useTranslation } from 'next-i18next';

import styles from './sale-catalog-filter-modal.module.scss';

import { OptionsBlock } from '../../catalog';

export const SaleCatalogFilterModal = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation('index');
  const {
    currency: { label, symbol, value: currency },
  } = useCurrency();

  const { toggleParam, query, resetQuery } = useSaleCatalogQuery();

  const catalog = useSaleQuery(query, label);
  const data = catalog.data;
  if (!data) return null;
  const meta = data.meta;
  const fieldsMeta = data.fieldsMeta;
  const quantity = meta.totalElements;

  const getTranslatedOptions = getTranslatedOptionsFn(fieldsMeta, t);

  return (
    <div className={styles.modal}>
      <div className={styles.modal__top}>
        <P2>{t('allFilter')}</P2>
      </div>
      <div className={styles.modal__content}>
        <div className={styles.modal__contentLeft}>
          <OptionsBlock
            items={getTranslatedOptions(SALE_OPTIONS.type, 'type')}
            catalogQuery="type"
            type="sale"
          />
          <div
            className={clsx(
              styles.modal__filterBlock,
              styles['modal__filterBlock--district']
            )}
          >
            <Button
              label={t('islandDistrictAndBeach')}
              theme="dark-invert"
              size="lg"
              as="button"
              className={styles.modal__district}
              icon={<MapPinIcon />}
              onClick={() => {
                modal.open(<SaleCatalogMap includeSelectAll />, {
                  isMapModal: true,
                  hideCloseButton: true,
                });
              }}
            />
          </div>
          <div className={styles.modal__filterBlock}>
            <P2 className={styles.modal__filterTitle}>{t('cost')}</P2>
            <PriceControls
              currency={currency}
              symbol={symbol}
              meta={fieldsMeta}
              isInModal
            />
          </div>
          <div className={clsx(styles.modal__filterBlock)}>
            <P2 className={styles.modal__filterTitle}>{t('bedrooms')}</P2>
            <CheckboxBlock
              isButton
              items={getTranslatedOptions(
                SALE_OPTIONS.bedrooms,
                'bedroomsAmount'
              )}
              catalogQuery="bedroomsAmount"
              type="sale"
            />
          </div>
          <div className={clsx(styles.modal__filterBlock)}>
            <P2 className={styles.modal__filterTitle}>
              {t('catalog.bathroomAmount')}
            </P2>
            <CheckboxBlock
              isButton
              items={getTranslatedOptions(
                SALE_OPTIONS.bathrooms,
                'bathroomAmount'
              )}
              catalogQuery="bathroomAmount"
              type="sale"
            />
          </div>
          <OptionsBlock
            items={getTranslatedOptions(
              SALE_OPTIONS.beachDistance,
              'beachDistance'
            )}
            catalogQuery="beachDistance"
            type="sale"
          />
          <OptionsBlock
            items={getTranslatedOptions(
              SALE_OPTIONS.invest,
              'investAttractiveness'
            )}
            catalogQuery="investAttractiveness"
            type="sale"
          >
            <Button
              theme="grayBorder"
              size="md"
              label={t('perfectForInvest')}
              as="button"
              icon={<SvgPerfectInvest />}
              isIconLeft
              disabled={fieldsMeta.perfectInvest === 0}
              className={clsx(
                styles.modal__button,
                query.perfectInvest && styles['modal__button--active']
              )}
              onClick={() => toggleParam('perfectInvest')}
            />
          </OptionsBlock>
          <OptionsBlock
            items={getTranslatedOptions(
              SALE_OPTIONS.construction,
              'buildingStages'
            )}
            catalogQuery="buildingStages"
            type="sale"
          />
          <OptionsBlock
            items={getTranslatedOptions(SALE_OPTIONS.view, 'view')}
            catalogQuery="view"
            type="sale"
          />
          <OptionsBlock
            items={getTranslatedOptions(SALE_OPTIONS.landscape, 'landscape')}
            catalogQuery="landscape"
            type="sale"
          />
          <OptionsBlock
            items={getTranslatedOptions(
              SALE_OPTIONS.ownership,
              'ownershipType'
            )}
            catalogQuery="ownershipType"
            type="sale"
          />
          <Button
            theme="grayBorder"
            size="lg"
            label={t('videoReview')}
            as="button"
            disabled={fieldsMeta.videoReview === 0}
            className={clsx(
              styles.modal__button,
              query.videoReview && styles['modal__button--active']
            )}
            onClick={() => toggleParam('videoReview')}
          />
        </div>
        <div className={styles.modal__contentRight}>
          <SaleCatalogMap isInAllFilters />
          <div className={styles.modal__beaches}>
            <OptionsBlock
              items={getTranslatedOptions(
                SALE_OPTIONS.phuketBeaches,
                'phuketBeaches',
                'locations.'
              )}
              catalogQuery="phuketBeaches"
              title={t('phuketBeaches')}
              type="sale"
            />
            <OptionsBlock
              items={getTranslatedOptions(
                SALE_OPTIONS.samuiBeaches,
                'samuiBeaches',
                'locations.'
              )}
              catalogQuery="samuiBeaches"
              title={t('samuiBeaches')}
              type="sale"
            />
          </div>
          <OptionsBlock
            items={getTranslatedOptions(SALE_OPTIONS.facilities, 'facilities')}
            catalogQuery="facilities"
            type="sale"
          />
        </div>
      </div>
      <div className={styles.modal__bottom}>
        <Button
          as="button"
          size="md"
          theme="dark"
          disabled={quantity === 0}
          label={`${t('show')} ${quantity} ${t(
            getWordEnding(
              quantity,
              [
                'declinable.offer',
                'declinable.offers',
                'declinable.offersMore',
              ],
              language as 'ru' | 'en'
            )
          )}`}
          onClick={() => modal.close()}
        />
        <Button
          as="button"
          size="md"
          theme="secondary"
          label={t('reset')}
          onClick={resetQuery}
        />
      </div>
    </div>
  );
};
